import { Router } from '@lightningjs/sdk'

import { transparent } from '../../constants/colors'
import { AppConfig } from '../../services/appConfig'
import {
  LinkModelStylesEnum,
  MessageModelTypesEnum,
  TvCardStylesEnum,
  TvCardTypesEnum,
} from '../../types/flo-enums'
import { TvGridContainerItem, TvGridContainerModel } from '../../types/flo-experience'
import { PAGINATION_OFFSET } from '../../utils/carouselUtils'
import { createGridRows, toRowComponent, toRowRef } from '../../utils/gridUtils'
import { replaceQueryParam } from '../../utils/misc'
import { PaginateBlock } from '../PaginateBlock/PaginateBlock'
import { PaginationErrorMessage } from '../PartialErrorMessage/PaginationErrorMessage'
import SpeechComp from '../speechComp/speechComp'
import { TITLE_SAFE_MARGIN } from '../TvGridLayout/TvGridLayout'
import TvGridRow from './TvGridRow'

const WIDE_CARDS_COLUMNS_COUNT = 3
const NARROW_CARDS_COLUMNS_COUNT = 4
const MESSAGE_COLUMN_COUNT = 1
const TV_GRID_LAYOUT_HEIGHT = 1080
const TV_GRID_LAYOUT_HEIGHT_BOTTOM = 1036

export const injectPageNumber = (array = [], pageNumber = 0) => {
  return array.map((obj) => ({ ...obj, page: pageNumber }))
}

export default class TvGridContainer extends SpeechComp {
  private _data: TvGridContainerModel
  private _offsetYCoordinate: number
  private _currentRowIndex: number
  private _totalEl: number
  private _hasHeader: boolean
  private _headerHeight: number
  private _isCardFadeInEnabled: boolean = true
  private _cols: number
  private _hasPagination = false
  private _paginationError = false
  private _items: TvGridContainerItem[] = []
  private _isNextPagePending = false

  private _pageNumber = 0
  private _pageSize = 0
  private _paginationUrl = ''
  private _rowsPerPage = 0
  private _previousPage = 0
  private _isPagePending = false
  private _pageText = ''

  private _rowHeight = 0

  static _template() {
    return {
      w: (w) => w,
      rect: true,
      color: transparent,
      flex: { direction: 'column' },
      collision: true,
    }
  }

  get pageText(): string {
    return this._pageText
  }

  set pageText(v: string) {
    this._pageText = v
  }

  set data(v: TvGridContainerModel) {
    const willTilesDecrease = this._data && v?.items?.length < this._data.items?.length
    const prevTileIndex = willTilesDecrease ? this.currentChild.currentTile : null
    const prevRowIndex = willTilesDecrease ? this._currentRowIndex : null

    this._data = v
    this._items = injectPageNumber(v.items, this._pageNumber)
    this._cols = this.determineColumnCount(v.items)
    this._rowsPerPage = Math.floor(this._pageSize / this._cols)
    this._previousPage = this._pageNumber

    const { rows: individualRows, hasPagination } = createGridRows(
      v.items,
      this._cols,
      this._pageSize,
      this._pageNumber,
    )
    this._hasPagination = hasPagination
    const prevTotalRows = this._totalEl
    this._totalEl = individualRows.length - (this._hasPagination ? 1 : 0)

    this.childList.clear()
    this.patch({
      children: individualRows.map((row, i) => toRowComponent(row, i, this._isCardFadeInEnabled)),
    })
    this.patch({
      h: this.children.reduce((sum, child) => sum + child.h, 0) ?? 0,
    })

    this._rowHeight = this.children?.[0]?.h ?? 0

    if (willTilesDecrease && this._totalEl > 0) {
      this.determineRefocus(prevTileIndex, prevRowIndex, prevTotalRows)
    }
  }

  set currentRow(index: number) {
    this._currentRowIndex = index

    if (this.previousPageTrigger) {
      this.loadPreviousPage()
    }

    if (this.nextPageTrigger) {
      if (this._pageNumber === 0) {
        this.loadNextPage()
      } else {
        this.restoreNextPage()
      }
    }
  }

  set hasHeader(v: boolean) {
    this._hasHeader = v
  }

  get hasHeader() {
    return this._hasHeader
  }

  set headerHeight(height: number) {
    this._headerHeight = height
  }

  set isCardFadeInEnabled(isEnabled: boolean) {
    this._isCardFadeInEnabled = isEnabled
  }

  get data() {
    return this._data
  }

  get isFocusingFirstRow() {
    return this._currentRowIndex === 0
  }

  get isFocusingLastRow() {
    return this._currentRowIndex === this._totalEl - 1
  }

  get currentChild() {
    return this.childList.getByRef(toRowRef(this._currentRowIndex)) as TvGridRow
  }

  get offset() {
    return this._offsetYCoordinate
  }

  get currentRow() {
    return this._currentRowIndex
  }

  get totalEl() {
    return this._totalEl
  }

  get lastGridItems() {
    const pageNumber = Math.floor(this._currentRowIndex / this._rowsPerPage)

    const currentPageItems = this._items.filter((item: any) => item.page === pageNumber)

    return currentPageItems
  }

  get pageNumber() {
    return Math.floor(this._currentRowIndex / this._rowsPerPage)
  }

  set pageNumber(v) {
    this._pageNumber = v
  }

  set paginationUrl(v) {
    this._paginationUrl = v
  }

  set pageSize(v) {
    this._pageSize = v
  }

  get shouldLoadPreviousPage() {
    return this._currentRowIndex === this._rowsPerPage * this._previousPage + 1
  }

  get nextPageTrigger() {
    return this._currentRowIndex % this._rowsPerPage === this._rowsPerPage - 1
  }

  get previousPageTrigger() {
    return this._currentRowIndex % this._rowsPerPage === 0
  }

  get items() {
    return this._items
  }

  get selectedIndex() {
    return this.currentRow * this._cols + this.currentChild.currentTile
  }

  _init() {
    this._currentRowIndex = this._offsetYCoordinate = 0
  }

  _getFocused() {
    return this.currentChild
  }

  _handleDown() {
    this.handleScroll({ deltaY: 1 })
  }

  _handleUp() {
    if (this.isFocusingFirstRow) {
      // Bubbles up the event so the parent component can act on it
      return false
    }

    this.handleScroll({ deltaY: -1 })
  }

  _handleLeft() {
    if (Router.getActivePage().widgets.menu.visible) {
      Router.focusWidget('Menu')
    } else {
      return false
    }
  }

  handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0

    if (
      !isGoingDown &&
      this.currentChild instanceof PaginationErrorMessage &&
      this.currentChild.isPreviousPageError
    )
      return

    if ((!isGoingDown && this.isFocusingFirstRow) || (isGoingDown && this.isFocusingLastRow)) {
      return
    }

    if (!isGoingDown && this._isPagePending) return

    if (!isGoingDown && this.shouldLoadPreviousPage) {
      this.loadPreviousPage()
    }

    const oldRow = this.currentChild

    this._currentRowIndex += isGoingDown ? 1 : -1

    // new child is determined since this._currentRowIndex just changed
    const newRow = this.currentChild

    newRow.currentTile =
      oldRow.currentTile > newRow.totalEl - 1 ? newRow.totalEl - 1 : oldRow.currentTile

    this.determineOffset()
  }

  determineOffsetWithHeader({ gridShrunk } = { gridShrunk: false }) {
    if (
      this.currentChild instanceof PaginationErrorMessage &&
      this.currentChild.isPreviousPageError
    ) {
      this._offsetYCoordinate += this._rowHeight
      return
    }

    const currentRowYCoordinate = this.currentChild.finalY
    const currentRowHeight = this.currentChild.rowHeight
    const currentRowMarginBottom = this.currentChild.rowMarginBottom
    const topCoordinate = 0
    // In case the grid shrinks, we anticipate that the scrolling area is now smaller, now considering the lowest point in the grid
    // as one row above where it currently is
    const finalH = gridShrunk
      ? this.finalH - (currentRowHeight + currentRowMarginBottom)
      : this.finalH
    const bottomCoordinate = finalH - TV_GRID_LAYOUT_HEIGHT + this._headerHeight

    if (this._currentRowIndex === 0) {
      this._offsetYCoordinate = topCoordinate
    } else if (this._currentRowIndex === this._totalEl - 1 && bottomCoordinate > 0) {
      if (this._hasPagination && !this._paginationError) {
        this.loadNextPage()
      }
      this._offsetYCoordinate = -bottomCoordinate - (this._hasPagination ? PAGINATION_OFFSET : 0)
    } else {
      let middleYCoordinate =
        currentRowYCoordinate -
        TV_GRID_LAYOUT_HEIGHT / 2 +
        currentRowHeight / 2 +
        this._headerHeight / 2

      if (middleYCoordinate < 0) {
        // This can only happen if the number of elements on the Edit Favorites grid decreases. This keeps the focus where it currently is
        // if the deletion happens somewhere in the middle of the grid. Otherwise, shifts the position at the top
        middleYCoordinate = this._currentRowIndex <= 1 ? topCoordinate : -this._offsetYCoordinate
      } else if (
        middleYCoordinate <= currentRowHeight / 2 ||
        bottomCoordinate - middleYCoordinate <= currentRowHeight / 2
      ) {
        // If the ends of the grid have a very small offset, then immediately assign the coordinates as if they had been reached
        middleYCoordinate = this._currentRowIndex <= 1 ? topCoordinate : bottomCoordinate
      }
      this._offsetYCoordinate = -middleYCoordinate
    }
  }

  determineOffsetWithoutHeader() {
    if (
      this.currentChild instanceof PaginationErrorMessage &&
      this.currentChild.isPreviousPageError
    ) {
      this._offsetYCoordinate += this._rowHeight
      return
    }

    const currentRowYCoordinate = this.currentChild.finalY
    const currentRowHeight = this.currentChild.rowHeight

    if (this.isFocusingLastRow && this._hasPagination && !this._paginationError) {
      this.loadNextPage()
    }

    if (this._currentRowIndex >= 0 && this._currentRowIndex <= 1) {
      this._offsetYCoordinate = TITLE_SAFE_MARGIN
    } else if (
      this._currentRowIndex >= this._totalEl - 2 &&
      this._currentRowIndex <= this._totalEl - 1
    ) {
      let bottomCoordinate = this.finalH - TV_GRID_LAYOUT_HEIGHT_BOTTOM
      this._offsetYCoordinate = -bottomCoordinate - (this._hasPagination ? PAGINATION_OFFSET : 0)
    } else {
      const middleYCoordinate =
        currentRowYCoordinate - TV_GRID_LAYOUT_HEIGHT / 2 + currentRowHeight / 2
      this._offsetYCoordinate = -middleYCoordinate
    }
  }

  determineOffset() {
    if (this._hasHeader) {
      this.determineOffsetWithHeader()
    } else {
      this.determineOffsetWithoutHeader()
    }

    this.scrollGrid()
  }

  scrollGrid() {
    this.setSmooth('y', this._offsetYCoordinate, {
      duration: 0.2,
    })
  }

  reset() {
    this._init()
    this.scrollGrid()
  }

  determineColumnCount(items: Array<TvGridContainerItem>) {
    const item = items[0]

    if (!item) {
      return NARROW_CARDS_COLUMNS_COUNT
    }

    const itemType = item.type
    const itemStyle = item.style

    if (itemType === MessageModelTypesEnum.Message) {
      return MESSAGE_COLUMN_COUNT
    } else if (
      itemType === TvCardTypesEnum.TvEventCard ||
      itemType === TvCardTypesEnum.TvVideoCard
    ) {
      return itemStyle === TvCardStylesEnum.Small
        ? NARROW_CARDS_COLUMNS_COUNT
        : WIDE_CARDS_COLUMNS_COUNT
    } else {
      return NARROW_CARDS_COLUMNS_COUNT
    }
  }

  determineRefocus(prevTileIndex: number, prevRowIndex: number, prevTotalRows: number) {
    if (this._totalEl === prevTotalRows) {
      this.currentChild.currentTile =
        prevTileIndex <= this.currentChild.totalEl - 1 ? prevTileIndex : prevTileIndex - 1
    } else if (this._totalEl < prevTotalRows) {
      const currentRowIndex = this._currentRowIndex

      const newRowIndex =
        this._currentRowIndex > this._totalEl - 1 ? prevRowIndex - 1 : this._currentRowIndex
      this._currentRowIndex = newRowIndex // Updating this._currentRowIndex will recalcuate this.currentChild when called

      // This allows to recover the focus on the tile the focus currently is when the grid elements reload. Otherwise,
      // the focus stays in the same row, but would always go to the first element, which is not what we want.
      this.currentChild.currentTile =
        currentRowIndex > this._totalEl - 1 ? this.currentChild.totalEl - 1 : prevTileIndex

      this.determineOffsetWithHeader({ gridShrunk: true })
      this.scrollGrid()
    }

    this._refocus()
  }

  async loadPreviousPage() {
    this._isPagePending = true

    if (this._previousPage > 0) {
      const url = replaceQueryParam(
        this._paginationUrl,
        'offset',
        (this._previousPage - 1) * this._pageSize,
      )

      const page = await AppConfig.shared.experienceApi.getCollection(url, true)

      if (page?.error) {
        this._isPagePending = false
        const errorIdx = this._currentRowIndex - 1
        const error = {
          h: this._rowHeight,
          type: PaginationErrorMessage,
          isPreviousPageError: true,
          ref: toRowRef(errorIdx),
          retryAction: () => {
            this._currentRowIndex = errorIdx + 1
            this._isPagePending = true
            this.determineOffset()
            this.loadPreviousPage()
          },
        }

        this.childList.replace(error, this.children[this._currentRowIndex - 1])
        return
      }

      const items = page?.container?.items ?? []

      const { rows } = createGridRows(items, this._cols)

      const startIndex = (this._previousPage - 1) * Math.trunc(this._rowsPerPage)

      const children = rows.map((row, i) =>
        toRowComponent(row, i + startIndex, this._isCardFadeInEnabled),
      )

      for (let i = 0; i < children.length; i++) {
        this.childList.replace(children[i], this.children[startIndex + i])
      }

      this._previousPage--
      this._items.unshift(...injectPageNumber(items, this._previousPage))
    }

    this._isPagePending = false
  }

  async loadNextPage(pageBlock?: PaginateBlock) {
    if (this._isNextPagePending) return

    const paginationLink = pageBlock ?? (this.childList.last as PaginateBlock)
    const hasPaginationStyle =
      paginationLink?.style === LinkModelStylesEnum.Paginate ||
      paginationLink?.style === LinkModelStylesEnum.Open

    if (hasPaginationStyle && paginationLink?.url) {
      this._isNextPagePending = true
      paginationLink.toggleLoader(true)
      const page = await AppConfig.shared.experienceApi.getTvGridPartial(paginationLink.url)
      const items = page?.data?.items

      if (items && items.length > 0) {
        this.loadPage({ items, isPaginateBlock: true, removeLast: true })
      } else if (items?.length === 0) {
        this._hasPagination = false
        paginationLink.toggleLoader(false)
        this.childList.remove(this.childList.last)

        this._offsetYCoordinate += PAGINATION_OFFSET
        this.scrollGrid()
      } else if (page?.error) {
        const error = {
          type: PaginationErrorMessage,
          ref: toRowRef(this._totalEl),
          retryAction: () => {
            this._currentRowIndex = this._totalEl - 2
            this._paginationError = false
            this._totalEl--
            this.loadNextPage(paginationLink)
          },
        }

        this.childList.replace(error, this.childList.last)
        this._totalEl++
        this._paginationError = true
      }

      this._isNextPagePending = false
    }
  }

  async restoreNextPage() {
    const url = replaceQueryParam(
      this._paginationUrl,
      'offset',
      (this._pageNumber + 1) * this._pageSize,
    )

    const page = await AppConfig.shared.experienceApi.getCollection(url)
    const items = page?.container?.items ?? []

    if (items && items.length > 0) {
      this.loadPage({ items })
    }
  }

  async loadPage({ items = [], isPaginateBlock = false, removeLast = false }) {
    this._hasPagination = false

    const startIndex = isPaginateBlock ? this._totalEl : this._currentRowIndex + 1
    const { rows, hasPagination } = createGridRows(items, this._cols)

    const children = rows.map((row, i) =>
      toRowComponent(row, startIndex + i, this._isCardFadeInEnabled),
    )

    const newHeight = children.reduce((sum, child) => sum + child.h, 0) ?? 0
    this.patch({ h: this.h + newHeight })

    if (removeLast) this.childList.remove(this.childList.last)

    this.childList.a(children)
    this._hasPagination = hasPagination
    this._totalEl += children.length - (this._hasPagination ? 1 : 0)
    this._pageNumber++

    const newItems = injectPageNumber(items, this._pageNumber)
    if (isPaginateBlock) {
      this._items.splice(-1, 1, ...newItems)
    } else {
      this._items = [...this._items, ...newItems]
    }

    this.fireAncestors('$nextPageLoadComplete')
  }
}
